/**
 * Manages the TrixChar Spine mascot (v2 assets) split across two z-layers:
 *   containerBack  — rendered behind the PixiJS wheel (body, cape, hat)
 *   containerFront — rendered in front of the PixiJS wheel (right arm, wheel effects)
 *
 * Both Spine instances are loaded from the same skeleton and play identical
 * animations in lock-step. Slot visibility per layer is controlled by
 * FRONT_SLOT_NAMES.
 *
 * Track 0 (mascot body):
 *   mini_idle    — base loop in WAITING / BETTING (mini family)
 *   mini_action  — one-shot reaction on each new bet, then back to mini_idle
 *   transform_0  — mini → big transition (one-shot, played on entering SPINNING)
 *   big_idle     — base loop from SPINNING through SLOWING / RESOLVED (big family)
 *   big_trick    — one-shot reaction on each trick application, then back to big_idle
 *   transform_1  — big → mini transition (one-shot, played on InitWheelScene of next round)
 *
 * Track 1 (wheel effects):
 *   freeze_start → freeze_loop  — during FREEZE; reversed freeze_start after the freeze
 *     overlay expires (`TRICK_FREEZE_DURATION_MS`, stacked total from WheelEngine via
 *     `playFreezeStart(remainingMs)`) OR when finalize/resolve calls endFreeze().
 *   fire_start   → fire_loop    — PUSH trick active for TRICK_BOOST_DURATION_MS
 *                                 (auto-ended via internal timer with reversed fire_start).
 *                                 При несовпадении `WHEEL_VISUAL_SPIN_SIGN` и
 *                                 `PUSH_TRICK_VFX_AUTHORED_SPIN_SIGN` track 1 (`fire_*`)
 *                                 играет в reverse — палец (track 0) не зеркалится.
 *
 * The `frozze_wheel` slot is hidden because the actual wheel is rendered by
 * WheelEngine (PixiJS sectors). The `wheel` slot was removed in v2 assets.
 */

import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { Assets, Container, Ticker } from 'pixi.js';
import { PUSH_TRICK_VFX_AUTHORED_SPIN_SIGN } from '@/shared/constants/pvpRoulette';
import { getPvpRouletteRuntime } from '@/shared/lib/pvp-roulette-runtime/runtime';

// Asset directory is bumped (v2 → v3) when the skeleton changes incompatibly,
// so CDN/Telegram cache holding the old path can never serve stale data.
const ATLAS_KEY = 'trix-char-v3-atlas';
const SKEL_KEY = 'trix-char-v3-skel';
const ATLAS_URL = '/spine/trix-char-v3/TrixChar_wip.atlas';
const SKEL_URL = '/spine/trix-char-v3/TrixChar_wip.json';

function addAssetSafe(alias: string, src: string): void {
  try {
    Assets.add({ alias, src });
  } catch {
    // duplicate alias on re-mount cycle — safe to ignore
  }
}

// Hidden in both instances — PixiJS renders the real wheel on top.
const HIDDEN_SLOT_NAMES = ['wheel', 'frozze_wheel'];

// Slots rendered in the FRONT layer (above the PixiJS wheel).
const FRONT_SLOT_NAMES: string[] = [
  'finger_selector',
  'vfx_light_01', 'vfx_light_02', 'vfx_light_03', 'vfx_light_04',
  'fire_vfx', 'fire_vfx2', 'fire_vfx3', 'fire_vfx4',
  'glow', 'glow2', 'glow3', 'glow4', 'glow5', 'glow6', 'glow7', 'glow8', 'glow9',
  'glow_vfx', 'glow_vfx2', 'glow_vfx3', 'glow_vfx4', 'glow_vfx5', 'glow_vfx6',
  'glow_vfx7', 'glow_vfx8', 'glow_vfx9', 'glow_vfx10', 'glow_vfx11', 'glow_vfx12',
  'glow_vfx13', 'glow_vfx14', 'glow_vfx15', 'glow_vfx16', 'glow_vfx17',
  'glow_vfx18', 'glow_vfx19', 'glow_vfx20', 'glow_vfx21',
];

// Spine `wheel` attachment diameter — base for scale derivation.
const WHEEL_DESIGN_DIAMETER = 1254;
const MASCOT_SCALE_FACTOR = 0.5;
// Skeleton-local (Y-up) anchor of the wheel-effects area — calibrated visually
// against the emulator. Do NOT replace with dynamically-read bone values; the
// designer's tuning of MASCOT_X_OFFSET / MASCOT_Y_OFFSET is paired with these.
const WHEEL_SLOT_OFFSET_X = -35.15;
const WHEEL_SLOT_OFFSET_Y = -48.46;
const MASCOT_X_OFFSET = -1;
const MASCOT_Y_OFFSET = 14;

const DEFAULT_ANIMATION_MIX_SEC = 0.15;

type Pose = 'mini' | 'big';

export class TrixCharManager {
  readonly containerBack: Container;
  readonly containerFront: Container;

  private spineBack: Spine | null = null;
  private spineFront: Spine | null = null;
  private freezeActive = false;
  private loading = false;
  private loaded = false;
  // Drives transform_0 / transform_1 insertion when crossing pose families.
  private currentPose: Pose = 'mini';
  // Tracks the currently looping target on Track 0 — lets playToFamily skip a no-op
  // re-`setAnimation` that would restart the loop from time 0.
  private currentTargetAnimation: string | null = null;
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  /** Clears Spine freeze overlay when freeze physics expires (manual TRICKS can skip finalize). */
  private freezeClearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.containerBack = new Container();
    this.containerFront = new Container();
    this.containerFront.visible = FRONT_SLOT_NAMES.length > 0;
  }

  async load(wheelCenterX: number, wheelCenterY: number, outerRadius: number): Promise<void> {
    if (this.loaded) {
      this.reposition(wheelCenterX, wheelCenterY, outerRadius);
      return;
    }
    if (this.loading) return;
    this.loading = true;

    try {
      addAssetSafe(ATLAS_KEY, ATLAS_URL);
      addAssetSafe(SKEL_KEY, SKEL_URL);
      await Assets.load([ATLAS_KEY, SKEL_KEY]);

      const spineBack = Spine.from({ skeleton: SKEL_KEY, atlas: ATLAS_KEY, autoUpdate: true });
      const spineFront = Spine.from({ skeleton: SKEL_KEY, atlas: ATLAS_KEY, autoUpdate: true });
      this.spineBack = spineBack;
      this.spineFront = spineFront;
      this.loaded = true;

      for (const spine of [spineBack, spineFront]) {
        for (const name of HIDDEN_SLOT_NAMES) {
          const slot = spine.skeleton.findSlot(name);
          if (slot) slot.color.a = 0;
        }
        spine.state.data.defaultMix = DEFAULT_ANIMATION_MIX_SEC;
      }

      if (FRONT_SLOT_NAMES.length > 0) {
        for (const name of FRONT_SLOT_NAMES) {
          const slot = spineBack.skeleton.findSlot(name);
          if (slot) slot.color.a = 0;
        }
        const frontSet = new Set(FRONT_SLOT_NAMES);
        for (const slot of spineFront.skeleton.slots) {
          if (!frontSet.has(slot.data.name)) slot.color.a = 0;
        }
      }

      this.reposition(wheelCenterX, wheelCenterY, outerRadius);
      this.containerBack.addChild(spineBack);
      this.containerFront.addChild(spineFront);

      // Spine autoUpdate relies on Ticker.shared running.
      if (!Ticker.shared.started) Ticker.shared.start();

      this.playMiniIdle();
    } catch (err) {
      console.error('[TrixCharManager] Failed to load Spine assets:', err);
      this.loading = false;
    }
  }

  reposition(wheelCenterX: number, wheelCenterY: number, outerRadius: number): void {
    const scale = (outerRadius * 2) / WHEEL_DESIGN_DIAMETER * MASCOT_SCALE_FACTOR;
    const x = wheelCenterX - WHEEL_SLOT_OFFSET_X * scale + MASCOT_X_OFFSET;
    const y = wheelCenterY + WHEEL_SLOT_OFFSET_Y * scale + MASCOT_Y_OFFSET;
    for (const spine of [this.spineBack, this.spineFront]) {
      if (!spine) continue;
      spine.scale.set(scale);
      spine.x = x;
      spine.y = y;
    }
  }

  private shouldMirrorPushFire(): boolean {
    return (
      getPvpRouletteRuntime().WHEEL_VISUAL_SPIN_SIGN !==
      PUSH_TRICK_VFX_AUTHORED_SPIN_SIGN
    );
  }

  /** Reverse only track-1 `fire_*` entries — finger / track 0 stay untouched. */
  private applyPushFireTrackMirror(spine: Spine, mirrored: boolean): void {
    let entry = spine.state.getCurrent(1);
    while (entry) {
      const name = entry.animation?.name ?? '';
      if (name.startsWith('fire_')) {
        entry.reverse = mirrored;
        entry.trackTime = mirrored ? entry.animationEnd : 0;
      }
      entry = entry.next;
    }
  }

  private forBoth(fn: (spine: Spine) => void): void {
    if (this.spineBack) fn(this.spineBack);
    if (this.spineFront) fn(this.spineFront);
  }

  /**
   * Safe wrapper around `state.setAnimation` — silently skips when the named
   * animation is missing from the loaded skeleton. Without this guard, an
   * outdated cached asset (e.g., after Telegram cache wipe re-fetching a stale
   * version) would crash the app via the Configuration Error boundary.
   */
  private setAnimationSafe(spine: Spine, track: number, name: string, loop: boolean): ReturnType<Spine['state']['setAnimation']> | null {
    if (!spine.skeleton.data.findAnimation(name)) {
      console.warn(`[TrixCharManager] missing animation "${name}" — skipping`);
      return null;
    }
    return spine.state.setAnimation(track, name, loop);
  }

  private addAnimationSafe(spine: Spine, track: number, name: string, loop: boolean, delay: number): void {
    if (!spine.skeleton.data.findAnimation(name)) {
      console.warn(`[TrixCharManager] missing animation "${name}" — skipping`);
      return;
    }
    spine.state.addAnimation(track, name, loop, delay);
  }

  private playToFamily(target: string, targetFamily: Pose): void {
    // Ignore calls before Spine is ready — otherwise we'd corrupt currentPose
    // and force a stale cross-family transform when load() finally runs.
    if (!this.spineBack && !this.spineFront) return;
    const sameFamily = this.currentPose === targetFamily;
    if (sameFamily && this.currentTargetAnimation === target) return;
    const isFirstAnimation = this.currentTargetAnimation === null;
    const transition = targetFamily === 'big' ? 'transform_0' : 'transform_1';
    this.forBoth((s) => {
      if (sameFamily || isFirstAnimation) {
        this.setAnimationSafe(s, 0, target, true);
      } else {
        this.setAnimationSafe(s, 0, transition, false);
        this.addAnimationSafe(s, 0, target, true, 0);
      }
    });
    this.currentPose = targetFamily;
    this.currentTargetAnimation = target;
  }

  playMiniIdle(): void {
    this.playToFamily('mini_idle', 'mini');
  }

  playBigIdle(): void {
    this.playToFamily('big_idle', 'big');
  }

  playTrickReaction(): void {
    if (this.currentPose !== 'big') return;
    this.forBoth((s) => {
      this.setAnimationSafe(s, 0, 'big_trick', false);
      this.addAnimationSafe(s, 0, 'big_idle', true, 0);
    });
  }

  playMiniAction(): void {
    if (this.currentPose !== 'mini') return;
    this.forBoth((s) => {
      this.setAnimationSafe(s, 0, 'mini_action', false);
      this.addAnimationSafe(s, 0, 'mini_idle', true, 0);
    });
  }

  private forFrontTrack(fn: (spine: Spine) => void): void {
    if (this.spineFront) fn(this.spineFront);
  }

  playFreezeStart(remainingMs?: number): void {
    if (!this.spineFront) return;
    const freezeMs = getPvpRouletteRuntime().TRICK_FREEZE_DURATION_MS;
    const ms = Math.max(
      1,
      remainingMs != null &&
        Number.isFinite(remainingMs) &&
        remainingMs > 0
        ? remainingMs
        : freezeMs,
    );
    this.freezeActive = true;
    this.clearFreezeClearTimer();
    this.forFrontTrack((s) => {
      this.setAnimationSafe(s, 1, 'freeze_start', false);
      this.addAnimationSafe(s, 1, 'freeze_loop', true, 0);
    });
    this.freezeClearTimer = setTimeout(() => {
      this.freezeClearTimer = null;
      this.endFreeze();
    }, ms);
  }

  private clearFreezeClearTimer(): void {
    if (this.freezeClearTimer) {
      clearTimeout(this.freezeClearTimer);
      this.freezeClearTimer = null;
    }
  }

  endFreeze(): void {
    this.clearFreezeClearTimer();
    if (!this.freezeActive) return;
    this.freezeActive = false;
    this.forFrontTrack((s) => {
      const entry = this.setAnimationSafe(s, 1, 'freeze_start', false);
      if (!entry) return;
      entry.reverse = true;
      entry.trackTime = entry.animationEnd;
      s.state.addEmptyAnimation(1, 0, 0);
    });
  }

  playPush(): void {
    if (!this.spineFront) return;
    this.clearPushTimer();
    const mirrorFire = this.shouldMirrorPushFire();
    this.forFrontTrack((s) => {
      this.setAnimationSafe(s, 1, 'fire_start', false);
      this.addAnimationSafe(s, 1, 'fire_loop', true, 0);
      if (mirrorFire) this.applyPushFireTrackMirror(s, true);
    });
    const durationMs = getPvpRouletteRuntime().TRICK_BOOST_DURATION_MS;
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null;
      this.forFrontTrack((s) => {
        const entry = this.setAnimationSafe(s, 1, 'fire_start', false);
        if (!entry) return;
        entry.reverse = true;
        entry.trackTime = entry.animationEnd;
        s.state.addEmptyAnimation(1, 0, 0);
      });
    }, durationMs);
  }

  private clearPushTimer(): void {
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }
  }

  /**
   * Returns the angle (PixiJS degrees: 0 = right, +90 = down, -90 = up) from the
   * wheel center to the big-mascot claw tip (`finger_selector2` bone) in stage coords.
   * Returns null while the skeleton is not loaded or the bone is missing.
   */
  getClawAngleDegFromCenter(wheelCenterStageX: number, wheelCenterStageY: number): number | null {
    const spine = this.spineFront ?? this.spineBack;
    if (!spine) return null;
    const bone = spine.skeleton.findBone('finger_selector2');
    if (!bone) return null;
    // worldX/worldY are kept up-to-date by Spine.autoUpdate via Ticker.shared.
    // Spine local frame is Y-up; Pixi-Spine flips Y at render time.
    const stageX = spine.x + bone.worldX * spine.scale.x;
    const stageY = spine.y - bone.worldY * spine.scale.y;
    const dx = stageX - wheelCenterStageX;
    const dy = stageY - wheelCenterStageY;
    if (dx === 0 && dy === 0) return null;
    return Math.atan2(dy, dx) * 180 / Math.PI;
  }

  /** Stop push/freeze track overlays when the wheel unmounts (engine stays alive). */
  pauseRouteDetachEffects(): void {
    this.clearPushTimer();
    this.endFreeze();
  }

  destroy(): void {
    this.clearPushTimer();
    this.clearFreezeClearTimer();
    this.containerBack.parent?.removeChild(this.containerBack);
    this.containerFront.parent?.removeChild(this.containerFront);
    if (this.spineBack) { this.spineBack.destroy(); this.spineBack = null; }
    if (this.spineFront) { this.spineFront.destroy(); this.spineFront = null; }
    this.containerBack.destroy({ children: true });
    this.containerFront.destroy({ children: true });
  }
}
